import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Typography, Input, Tabs, Spin, Tag, Tooltip, Card, Space, message } from "antd";
import { SearchOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Table } from "antd";

import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import {
  listQuickWords,
  upsertQuickWord,
  deleteQuickWord,
  publishQuickWord,
} from "./quick-word-service";
import QuickWordConfigDialog from "./QuickWordConfigDialog";
import QuickWordDeleteDialog from "./QuickWordDeleteDialog";
import QuickWordPublishDialog from "./QuickWordPublishDialog";

const EllipsisTooltipCell = ({ value, width = 150 }) => (
  <Tooltip title={value} placement="topStart">
    <Typography.Text
      ellipsis
      style={{
        maxWidth: width,
        display: "block",
      }}
    >
      {value}
    </Typography.Text>
  </Tooltip>
);

function QuickWordView() {
  const { apiCall } = useApiCall();
  const { successToast, errorToast } = useToast();

  const [commands, setCommands] = useState([]);
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("prod");

  const [selectedCommand, setSelectedCommand] = useState(null);
  const [formData, setFormData] = useState(null);
  const [jsonContent, setJsonContent] = useState("");
  const [jsonError, setJsonError] = useState("");

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const [isProcessing, setIsProcessing] = useState(null);
  const defaultCommand = {
    id: "",
    version: "stg",
    provider: "",
    keyword: "",
    items: [
      {
        quickWord: "",
        regex: "",
        vars: [],
      },
    ],
    varTypes: [
      {
        varName: "",
        varType: "",
      },
    ],
    descriptions: [
      {
        lang: "EN",
        description: "",
      },
    ],
    script: "",
  };

  const baseCommandSchema = {
    type: "object",
    properties: {
      id: {
        type: "string",
        title: "Command ID",
        description: "Unique identifier for the quick command",
      },
      version: {
        type: "string",
        title: "Version",
        enum: ["stg", "prod"],
        default: "stg",
        description: "Command version (auto-set by service)",
      },
      provider: {
        type: "string",
        title: "Provider",
        description: "Service provider for this command (auto-set by service)",
      },
      keyword: {
        type: "string",
        title: "Keyword",
        description: "Primary keyword that triggers this command",
      },
      items: {
        type: "array",
        title: "Quick Word Items",
        description: "Define quick word patterns with regex and variables",
        items: {
          type: "object",
          title: false,
          properties: {
            quickWord: {
              type: "string",
              title: "Quick Word",
              description: "Quick word pattern for command matching",
            },
            regex: {
              type: "string",
              title: "Regex Pattern",
              description:
                "Regular expression pattern (auto-generated by service)",
            },
            vars: {
              type: "array",
              title: "Variables",
              description:
                "Variable names extracted from this pattern (auto-generated by service)",
              items: {
                type: "string",
              },
            },
          },
        },
      },
      varTypes: {
        type: "array",
        title: "Variable Types",
        description: "Define variable types for the extracted variables",
        items: {
          type: "object",
          title: false,
          properties: {
            varName: {
              type: "string",
              title: "Variable Name",
              description: "Name of the variable",
            },
            varType: {
              type: "string",
              title: "Variable Type",
              description: "Type of the variable (e.g., string, number, date)",
            },
          },
        },
      },
      descriptions: {
        type: "array",
        title: "Descriptions",
        description: "Descriptions of the command in different languages",
        items: {
          type: "object",
          title: false,
          properties: {
            lang: {
              type: "string",
              title: "Language",
              enum: [
                "EN",
                "ZH",
                "ES",
                "HI",
                "AR",
                "PT",
                "BN",
                "RU",
                "JA",
                "FR",
                "DE",
              ],
              enumNames: [
                "English (EN)",
                "Chinese (ZH)",
                "Spanish (ES)",
                "Hindi (HI)",
                "Arabic (AR)",
                "Portuguese (PT)",
                "Bengali (BN)",
                "Russian (RU)",
                "Japanese (JA)",
                "French (FR)",
                "German (DE)",
              ],
            },
            description: {
              type: "string",
              title: "Description",
            },
          },
          required: ["lang", "description"],
        },
        minItems: 1,
      },
      script: {
        type: "string",
        title: "Execute Script",
        description: "Script to execute when the command matches",
      },
    },
  };

  const baseUiSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    keyword: {
      "ui:placeholder": "Enter primary keyword",
      "ui:description": "Primary keyword that triggers this command",
    },
    items: {
      "ui:addButtonLabel": "Add Quick Word Item",
      "ui:description":
        "Add multiple quick word items with patterns and variables",
      items: {
        "ui:title": false,
        quickWord: {
          "ui:widget": "textarea",
          "ui:options": {
            rows: 3,
          },
          "ui:placeholder": "Enter quick word pattern",
          "ui:description":
            "Quick word pattern for matching (regex and vars will be auto-generated)",
        },
        regex: {
          "ui:widget": "textarea",
          "ui:options": {
            rows: 2,
          },
          "ui:placeholder": "Auto-generated regex pattern",
          "ui:description":
            "Regular expression pattern (auto-generated by service)",
          "ui:readonly": true,
          "ui:disabled": true,
        },
        vars: {
          "ui:addButtonLabel": "Add Variable",
          "ui:placeholder": "Enter variable name",
          "ui:description":
            "Variable names that will be extracted from the pattern (auto-generated by service)",
          "ui:readonly": true,
          "ui:disabled": true,
        },
      },
    },
    varTypes: {
      "ui:addButtonLabel": "Add Variable Type",
      "ui:description":
        "Define types for variables that will be extracted from patterns",
      items: {
        "ui:title": false,
        varName: {
          "ui:placeholder": "Enter variable name",
          "ui:description":
            "Name of the variable (should match extracted variables)",
        },
        varType: {
          "ui:placeholder": "Enter variable type",
          "ui:description":
            "Type of the variable (e.g., string, number, date, boolean)",
        },
      },
    },
    descriptions: {
      "ui:addButtonLabel": "Add Language Description",
      "ui:description":
        "Add descriptions for the command in different languages. At least one description is required.",
      "ui:title": "Multi-language Descriptions",
      items: {
        "ui:title": false,
        "ui:order": ["lang", "description"],
        lang: {
          "ui:widget": "select",
          "ui:placeholder": "Select language",
          "ui:description": "Choose the language for this description",
        },
        description: {
          "ui:widget": "textarea",
          "ui:options": {
            rows: 3,
          },
          "ui:placeholder": "Enter description in the selected language",
          "ui:description":
            "Description of the command in the selected language",
        },
      },
    },
  };

  const baseUiSchemaWithScriptEditor = useMemo(
    () => ({
      ...baseUiSchema,
      script: {
        "ui:widget": "CodeEditorWidget",
        "ui:description":
          "Script to execute when the command matches (supports YAML/JavaScript syntax)",
      },
    }),
    [],
  );

  const createUiSchema = useMemo(
    () => ({
      ...baseUiSchemaWithScriptEditor,
      version: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Version will be auto-set by service",
      },
      provider: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Provider will be auto-set by service",
      },
    }),
    [baseUiSchemaWithScriptEditor],
  );

  const editUiSchema = useMemo(
    () => ({
      ...baseUiSchemaWithScriptEditor,
      id: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "ID cannot be modified",
      },
      version: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Version cannot be modified",
      },
      provider: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Provider cannot be modified",
      },
    }),
    [baseUiSchemaWithScriptEditor],
  );

  // Transform functions for varTypes between array (form) and object (backend)
  const transformVarTypesToArray = varTypesObj => {
    if (!varTypesObj || typeof varTypesObj !== "object") return [];
    return Object.entries(varTypesObj).map(([varName, varType]) => ({
      varName,
      varType,
    }));
  };

  const transformVarTypesToObject = varTypesArray => {
    if (!Array.isArray(varTypesArray)) return {};
    const result = {};
    varTypesArray.forEach(({ varName, varType }) => {
      if (varName && varType) {
        result[varName] = varType;
      }
    });
    return result;
  };

  // Transform command data for form display
  const transformCommandForForm = command => {
    if (!command) return null;
    return {
      ...command,
      varTypes: transformVarTypesToArray(command.varTypes),
    };
  };

  // Transform form data for backend
  const transformFormDataForBackend = formData => {
    if (!formData) return null;
    return {
      ...formData,
      varTypes: transformVarTypesToObject(formData.varTypes),
    };
  };

  useEffect(() => {
    fetchCommands();
  }, []);

  useEffect(() => {
    if (commands.length > 0) {
      filterCommands();
    }
  }, [searchTerm, activeTab, commands]);

  useEffect(() => {
    if (!configDialogOpen && !deleteDialogOpen && !publishDialogOpen) {
      setIsProcessing(null);
    }
  }, [configDialogOpen, deleteDialogOpen, publishDialogOpen]);

  const fetchCommands = async () => {
    setLoading(true);
    try {
      const resp = await listQuickWords(apiCall);
      if (resp.schemas) {
        setCommands(resp.schemas);
        filterCommands(resp.schemas);
      }
    } catch (error) {
      errorToast("Failed to load commands");
      console.error("Failed to load commands:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterCommands = (data = commands) => {
    let filtered = data.filter(cmd => cmd.version === activeTab);

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(cmd => {
        const id = cmd.id.toLowerCase();
        const keyword = cmd.keyword ? cmd.keyword.toLowerCase() : "";

        return id.includes(query) || keyword.includes(query);
      });
    }

    setFilteredCommands(filtered);
  };

  const handleCreateNew = () => {
    setIsProcessing("create");
    setTimeout(() => {
      const formCommand = transformCommandForForm(defaultCommand);
      setSelectedCommand(defaultCommand);
      setFormData(formCommand);
      setJsonContent(JSON.stringify(defaultCommand, null, 2));
      setConfigDialogOpen(true);
      setIsProcessing(null);
    }, 500);
  };

  const handleEditCommand = command => {
    const formCommand = transformCommandForForm(command);
    setSelectedCommand(command);
    setFormData(formCommand);
    setJsonContent(JSON.stringify(command, null, 2));
    setConfigDialogOpen(true);
  };

  const handleDeleteClick = command => {
    setSelectedCommand(command);
    setDeleteDialogOpen(true);
  };

  const handlePublishClick = command => {
    setSelectedCommand(command);
    setPublishDialogOpen(true);
  };

  const handleCloseConfigDialog = () => {
    setConfigDialogOpen(false);
    setSelectedCommand(null);
    setFormData(null);
    setJsonContent("");
    setJsonError("");
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCommand(null);
  };

  const handleClosePublishDialog = () => {
    setPublishDialogOpen(false);
    setSelectedCommand(null);
  };

  const handleFormChange = ({ formData: newFormData }) => {
    setFormData(newFormData);
    // Update JSON content when form changes - transform to backend format for JSON display
    if (newFormData) {
      const backendData = transformFormDataForBackend(newFormData);
      setJsonContent(JSON.stringify(backendData, null, 2));
      setJsonError(""); // Clear any JSON errors since form is valid
    }
  };

  const handleJsonChange = useCallback(instance => {
    const jsonText = instance.getValue();
    setJsonContent(jsonText);

    // Try to parse and update form data
    try {
      const parsedData = JSON.parse(jsonText);
      const formData = transformCommandForForm(parsedData);
      setFormData(formData);
      setJsonError(""); // Clear any previous errors
    } catch (error) {
      // Invalid JSON, don't update form data
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  }, []);

  const handleSaveCommand = async () => {
    if (!formData) return;

    setIsProcessing("save");
    try {
      const response = await upsertQuickWord(
        apiCall,
        transformFormDataForBackend(formData),
      );

      if (response && response.success) {
        successToast("Command saved successfully");

        await fetchCommands();
      } else {
        errorToast(response?.message || "Failed to save command");
      }

      handleCloseConfigDialog();
    } catch (error) {
      errorToast("Failed to save command");
      console.error("Failed to save command:", error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDeleteCommand = async () => {
    if (!selectedCommand) return;

    try {
      const { id, version } = selectedCommand;
      setIsProcessing("delete");

      const response = await deleteQuickWord(apiCall, id, version);

      if (response && response.success) {
        successToast("Command deleted successfully");

        await fetchCommands();
      } else {
        errorToast(response?.message || "Failed to delete command");
      }

      handleCloseDeleteDialog();
    } catch (error) {
      errorToast("Failed to delete command");
      console.error("Failed to delete command:", error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handlePublishCommand = async () => {
    if (!selectedCommand) return;

    setIsProcessing("publish");
    try {
      const { id } = selectedCommand;
      setIsProcessing("publish");

      const response = await publishQuickWord(apiCall, id);

      if (response && response.success) {
        successToast("Command published successfully");

        await fetchCommands();
      } else {
        errorToast(response?.message || "Failed to publish command");
      }

      handleClosePublishDialog();
    } catch (error) {
      errorToast("Failed to publish command");
      console.error("Failed to publish command:", error);
    } finally {
      setIsProcessing(null);
    }
  };
  const handleRefreshCommands = async () => {
    setIsProcessing("refresh");
    try {
      await fetchCommands();
      successToast("Commands refreshed");
    } catch (error) {
      errorToast("Failed to refresh commands");
    } finally {
      setIsProcessing(null);
    }
  };

  // const handleReloadCommands = async () => {
  //   setIsProcessing('reload');
  //   try {
  //     await reloadCommands(apiCall);
  //     successToast('Commands reloaded in the system');
  //   } catch (error) {
  //     errorToast('Failed to reload commands');
  //     console.error('Failed to reload commands:', error);
  //   } finally {
  //     setIsProcessing(null);
  //   }
  // };

  const columns = [
    {
      title: "Command ID",
      dataIndex: "id",
      key: "id",
      ellipsis: true,
      render: (text) => <EllipsisTooltipCell value={text} width={200} />,
    },
    {
      title: "Keyword",
      dataIndex: "keyword",
      key: "keyword",
      ellipsis: true,
      render: (text) => <EllipsisTooltipCell value={text} width={200} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space onClick={e => e.stopPropagation()}>
          {record.version === "stg" && (
              <Button
                size="small"
              type="primary"
                onClick={e => {
                  e.stopPropagation();
                handlePublishClick(record.original);
                }}
              >
                Publish
              </Button>
            )}
            <Button
              size="small"
            danger
              onClick={e => {
                e.stopPropagation();
              handleDeleteClick(record.original);
              }}
            >
              Delete
            </Button>
        </Space>
      ),
    },
  ];
  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <Card
      style={{
        padding: 24,
        height: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography.Title level={3} style={{ marginBottom: 16 }}>
        Quick Command Manager
      </Typography.Title>

        <Tabs
        activeKey={activeTab}
          onChange={handleTabChange}
        style={{ marginBottom: 16 }}
        items={[
          { key: "prod", label: "Production Commands" },
          { key: "stg", label: "Stg Commands" },
        ]}
      />

      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Space wrap style={{ marginBottom: 8 }}>
          <Button
            onClick={handleRefreshCommands}
            disabled={isProcessing === "refresh"}
            loading={isProcessing === "refresh"}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateNew}
            disabled={isProcessing === "create"}
            loading={isProcessing === "create"}
          >
            Create Command
          </Button>
        </Space>
        <Input
          placeholder="Search commands..."
          value={searchTerm}
          onChange={handleSearchChange}
          prefix={<SearchOutlined />}
          suffix={
            searchTerm ? (
              <CloseOutlined
                onClick={handleClearSearch}
                style={{ cursor: "pointer" }}
              />
            ) : null
          }
          style={{ width: 300, height: "40px", fontSize: "14px" }}
        />
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ flex: 1, width: "100%", marginTop: 16, overflow: "hidden" }}>
          <Table
            columns={columns}
            dataSource={filteredCommands.map(cmd => ({
              key: `${cmd.id}-${cmd.version}`,
              id: cmd.id,
              version: cmd.version,
              keyword: cmd.keyword,
              original: cmd,
            }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50"],
            }}
            onRow={(record) => ({
              onClick: () => handleEditCommand(record.original),
              style: { cursor: "pointer" },
            })}
          />
        </div>
      )}

      <QuickWordConfigDialog
        open={configDialogOpen}
        onClose={handleCloseConfigDialog}
        selectedCommand={selectedCommand}
        formData={formData}
        onFormChange={handleFormChange}
        jsonContent={jsonContent}
        onJsonChange={handleJsonChange}
        jsonError={jsonError}
        isProcessing={isProcessing}
        onSave={handleSaveCommand}
        schema={baseCommandSchema}
        createUiSchema={createUiSchema}
        editUiSchema={editUiSchema}
      />

      <QuickWordDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        selectedCommand={selectedCommand}
        onDelete={handleDeleteCommand}
        isProcessing={isProcessing}
      />

      <QuickWordPublishDialog
        open={publishDialogOpen}
        onClose={handleClosePublishDialog}
        selectedCommand={selectedCommand}
        onPublish={handlePublishCommand}
        isProcessing={isProcessing}
      />
    </Card>
  );
}

export default QuickWordView;
